<script setup lang="ts">
	import type { DeactivationMethodContext } from '~/components/Content/deactivation-method.vue'
	import useEmployeesApi, { type StaffRemovalResponse } from '~/services/apis/employeesApi'
	import usePurchases from '~/services/apis/purchases-info'
	import type { ListContext } from '~/components/Shared/list-item.vue'
	import { resolve } from '~/utils/functions'

	export type deactivationForm = { terms: boolean; password: string; deactivation_type: string }
	const props = defineProps<{
		needToKnow?: string[]
		removalSummary?: ListContext
		submitting?: boolean
		useEmit?: boolean
		status?: string
		productCategory?: string
		productCode?: number
		withPolicyId?: boolean
		staffName?: string
	}>()
	const confirmDialog = defineModel<boolean>({ default: false })
	const { productCategoryType } = useUtils()
	const categoryType = computed(() => productCategoryType(props.productCategory, props.productCode))
	const planCaption = computed(() => {
		return categoryType.value?.isAuto ? 'vehicle' : categoryType.value?.isGadget ? 'gadget' : 'staff'
	})
	const emits = defineEmits<{
		(event: 'submit', payload: deactivationForm): void
		(event: 'submitted'): void
	}>()
	const listRecord = defineModel<string[]>('selectedIds', { default: [], required: false })
	const isTermsDialog = shallowRef(false)
	const submittingRecords = shallowRef(false)
	const computingRecords = shallowRef(false)
	const computedMetrics = shallowRef<(StaffRemovalResponse & { premimum?: number; renewal_date?: string }) | null>(
		null
	)
	const deactivateForm = reactive({
		terms: false,
		password: '',
		deactivation_type: ''
	})
	const { internationalNoFormat } = useCurrency()
	const computedMetricsMerge = computed(() => ({
		toBeDeactivated: computedMetrics.value?.total_staff_to_be_deactivated ?? listRecord.value.length ?? '0',
		currentlyCovered: computedMetrics.value?.total_staff_to_remain ?? '0',
		premium: computedMetrics.value?.premimum ?? 0,
		renewal_date: computedMetrics.value?.renewal_date ?? ''
	}))
	const removalBrief = computed<ListContext>(() => {
		if (props.removalSummary) {
			return props.removalSummary
		}
		let totalToBeRemoved = 'Total staff to be deactivated'
		let totalNowCovered = 'Total staff now covered'
		if (props?.withPolicyId) {
			totalToBeRemoved = `Total ${planCaption.value} to be deactivated`
			totalNowCovered = `Total ${planCaption.value} now covered`
		}
		return {
			[totalToBeRemoved]: internationalNoFormat(computedMetricsMerge.value.toBeDeactivated),
			[totalNowCovered]: internationalNoFormat(computedMetricsMerge.value.currentlyCovered),
			'New premium': {
				value: internationalNoFormat(computedMetricsMerge.value.premium, '₦'),
				pClass: 'text-primary-2 font-medium',
				disabled: !props?.withPolicyId
			},
			'Plan renewal date': {
				value: computedMetricsMerge.value.renewal_date,
				disabled: !props?.withPolicyId
			}
		}
	})
	const needToKnowSummary = computed(() => {
		if (props?.needToKnow && props.needToKnow.length > 0) {
			return props.needToKnow
		}
		if (props?.withPolicyId) {
			const autoNeeds = categoryType.value?.isAuto
				? [
						'If the vehicle’s cover period has not been fully exhausted, it will remain active on the NIID until the plan expires.'
					]
				: []
			return [
				`You are about to deactivate <span class='text-main font-bold'>${
					computedMetricsMerge.value.toBeDeactivated || 'a'
				} ${planCaption.value} ${
					planCaption.value === 'staff'
						? `polic${computedMetricsMerge.value.toBeDeactivated > 1 ? 'ies' : 'y'}`
						: ''
				} </span> from this plan.`,
				`This action will remove the policyholders (staff) from the plan, along with the ${
					categoryType.value?.isAuto ? 'vehicles' : categoryType.value?.isGadget ? 'gadgets' : 'dependants'
				} covered.`,
				'You can choose to deactivate a policy instantly or keep it till the end of the renewal cycle',
				`<span class='text-error-500 italic font-[700]'>An instant deactivation</span> will deactivate this staff polic${
					computedMetricsMerge.value.toBeDeactivated > 1 ? 'ies' : 'y'
				} from this plan immediately.`,
				"<span class='text-warning-500 italic font-[700]'>Pending deactivation</span> will deactivate the policies at the end of the renewal cycle.",
				...autoNeeds,
				"All deactivated policies will be moved to the section termed “<span class='text-main italics font-semibold'>Deactivated policies</span>”.",
				'Deactivated policyholders can be re-added to any plan at any time.'
			]
		}
		if (props?.staffName) {
			return [
				`You are about to deactivate this staff <span class='text-gray-600 font-bold capitalize'>${props.staffName}</span> policies.`,
				'Note that the staff will be removed from all plans purchased for them in your dashboard.',
				'This action will also remove all dependants attached to this staff.'
			]
		}

		return [
			`You are about to deactivate <span class="text-primary-2">${internationalNoFormat(
				computedMetricsMerge.value.toBeDeactivated
			)} staff </span> from their policies.`,
			'These staff will be removed from all plans that cover them, their dependants will also be removed.',
			'You can choose to deactivate a policy instantly or keep it till the end of the end of the first month renewal cycle.',
			'<span class="text-error-500 italic font-[700]">An instant deactivation</span> will deactivate the staff from the plans that cover them immediately.',
			'<span class="text-warning-500 italic font-[700]">Pending deactivation</span> will deactivate this staff policies at the end of the cover period.',
			'Staff whose policies have been deactivated will move to the section termed “<span class="text-primary-2 font-semibold">Deactivated policies</span>”.',
			'Deactivated policies can be added to your plan at any time.'
		]
	})
	const { removePolicySummary, removePendingPolicySummary } = usePurchases()
	const { deactivateEmployeePolicies, removeEmployee } = useEmployeesApi()
	const header = computed(() => {
		if (props.withPolicyId) {
			return `Deactivate ${planCaption.value}`
		}
		return `Deactivate ${props.staffName ? 'Staff' : ''} Policies`
	})
	const policyMethod = computed<DeactivationMethodContext | null>(() => {
		if (props.withPolicyId) {
			return [
				{
					key: 'instant',
					title: {
						value: 'Instant Deactivation',
						className: 'text-error-500'
					},
					description: {
						value: 'This option will immediately remove the <span class="text-main font-medium"> selected policies </span> from the chosen plan. Deactivation is effective right away, meaning they will no longer have access to that plan. <br /> This action only affects the specific policy selected; the staff will retain any other policies they are still enrolled in.'
					}
				},
				{
					key: 'pending',
					title: {
						value: 'Pending Deactivation',
						className: 'text-warning-500'
					},
					description: {
						value: 'With this option, the <span class="text-main font-medium"> selected policies </span> will continue to have plan coverage until the end of the first month of the renewal cycle. <br /> <br /> Once this month concludes, their access to the plan will automatically be terminated.'
					},
					readonly: props.status === 'expired'
				}
			]
		}
		return null
	})
	const deactivateStaffPolicy = async () => {
		if (props.useEmit) {
			return emits('submit', deactivateForm)
		}
		resolve(async () => {
			const deactivation_type = props.withPolicyId ? 'policy_ids' : 'employee_ids'
			const result = await deactivateEmployeePolicies('manual', {
				preview: false,
				[deactivation_type]: listRecord.value as string[],
				instant_deactivation: deactivateForm.deactivation_type === 'instant' || props.status === 'expired',
				password: deactivateForm.password
			})
			if (result.value) {
				useNotification().alert({
					status: result.value.responseCode,
					message: result.value.responseText
				})
				confirmDialog.value = false
				deactivateForm.terms = false
				listRecord.value = []
				emits('submitted')
			}
		}, submittingRecords)
	}
	const computedPolicyIds = async () => {
		return resolve(async () => {
			let result = null
			if (props.status === 'active') {
				const res = await removePolicySummary(listRecord.value)
				if (res?.data) {
					result = res.data
				}
			} else if (props.status === 'pending') {
				const res = await removePendingPolicySummary(listRecord.value)
				if (res?.data) {
					result = res.data
				}
			}
			return result
		}, computingRecords)
	}
	const computeFileRecord = async () => {
		// Computing records from manual perspective
		resolve(async () => {
			if (props?.withPolicyId) {
				const result = await computedPolicyIds()
				const { formatDate } = useCustomDate()
				if (result) {
					confirmDialog.value = true
					computedMetrics.value = {
						current_staff: result.total_policies_now_covered,
						total_staff_to_be_deactivated: result.total_policies_to_be_removed,
						total_staff_to_remain: result.total_policies_now_covered,
						premimum: result.new_plan_premium,
						renewal_date: result?.plan?.actual_renewal_date
							? formatDate(result.plan.actual_renewal_date, 'Do of MMMM, YYYY')
							: result.plan_renewal_date
								? formatDate(result.plan_renewal_date, 'Do of MMMM, YYYY')
								: ''
					}
				}
				return null
			}

			const response = await removeEmployee('manual', {
				preview: true,
				employee_ids: listRecord.value
			})
			if (response.value?.data) {
				computedMetrics.value = response.value.data
				confirmDialog.value = true
			}
		}, computingRecords)
	}

	watch(confirmDialog, e => {
		if (e === true) {
			computeFileRecord()
			deactivateForm.deactivation_type = props.status === 'expired' ? 'instant' : ''
		} else if (e === false) {
			deactivateForm.terms = false
			deactivateForm.deactivation_type = ''
			deactivateForm.password = ''
		}
	})
</script>

<template>
	<div>
		<dialogs-error
			v-model:show="confirmDialog"
			:header="header"
			:type="!deactivateForm.terms ? 'warning' : 'error'"
			:wrapper-class="[' rounded-sm space-y-8 w-full', { 'bg-gray-50 p-5': !deactivateForm.terms }]"
			:icon-props="deactivateForm.terms ? { icon: 'trash', dir: 'fill' } : null"
		>
			<template #paragraph>
				<div v-show="!deactivateForm.terms" class="text-left">
					<span class="text-sm text-blue-950">Need to know</span>
					<content-list-item-disc :list="needToKnowSummary" />
				</div>
			</template>
			<template #p>
				<content-deactivation-method
					v-show="deactivateForm.terms"
					v-model="deactivateForm.deactivation_type"
					:list="policyMethod"
				/>
				<shared-list-item
					v-show="!deactivateForm.terms"
					class="mt-5 border-purple-200 bg-purple-50"
					:lists="removalBrief"
					:loading="computingRecords"
				/>
				<a-form
					:model="deactivateForm"
					:validate-messages="antValidateMessages"
					@finish="deactivateStaffPolicy"
				>
					<a-form-item v-show="deactivateForm.terms" label="Password" required name="password" class="!mt-12">
						<a-input-password
							v-model:value="deactivateForm.password"
							autocomplete="new password"
							placeholder="Enter your password"
							class="custom-input w-full !bg-gray-50 !px-5"
						/>
					</a-form-item>

					<a-form-item v-show="!deactivateForm.terms" name="terms" required>
						<div class="mt-10 flex w-full items-center justify-center">
							<a-checkbox v-model:checked="deactivateForm.terms">
								<span class="text-gray-600">
									I acknowledge and agree to the
									<span
										role="button"
										aria-label="button"
										class="font-medium text-primary-2"
										@click="() => (isTermsDialog = true)"
									>
										Terms & Conditions
									</span>
								</span>
							</a-checkbox>
						</div>
					</a-form-item>

					<div class="flex w-full flex-wrap items-center justify-between gap-4 sm:mt-12 sm:gap-10">
						<ui-button
							name="back"
							variants="two"
							class="flex-1 justify-center"
							@click="() => (deactivateForm.terms = false)"
						>
							Back
						</ui-button>
						<ui-button
							type="submit"
							name="continue"
							:loading="submittingRecords || submitting"
							class="flex-1 justify-center bg-error-500 !text-white disabled:cursor-not-allowed disabled:!bg-error-300"
							:disabled="
								!deactivateForm.terms ||
								!deactivateForm.deactivation_type ||
								!deactivateForm.password ||
								submitting
							"
						>
							Deactivate
						</ui-button>
					</div>
				</a-form>
				<dialogs-terms-and-condition v-model:show="isTermsDialog" />
			</template>

			<template #button>
				<span />
			</template>
		</dialogs-error>
	</div>
</template>
